/**
 * CopilotChatProvider - Provider for the copilot chat using REST API
 *
 * This replaces the complex WebRTC-based RealtimeChatProvider with a simple
 * REST API implementation that communicates with the backend workflow engine.
 */

import React, { useState, useCallback, useMemo, useEffect, useRef, useContext } from 'react';
import http from 'src/services/http';
import auth from 'src/services/auth';
import { IMessage, ThreadRolesEnum } from 'src/lib/schemas';
import UserContext from 'src/contexts/user/UserContext';
import {
  CopilotChatContext,
  CopilotChatContextType,
  OnboardingState,
  CopilotLocation,
  CopilotServiceArea,
  CopilotCatalog,
  CopilotCustomerProfile,
  UIComponent,
  LocationModalPrefill,
  ServiceAreaModalPrefill,
  ProductModalPrefill,
  CustomerProfileModalPrefill,
} from './CopilotChatContext';
import { shouldShowPublicWebsite } from 'src/lib/utils/subdomain';

interface CopilotChatProviderProps {
  children: React.ReactNode;
  assistantAlias?: string;
}

interface ChatResponse {
  message: string;
  conversationId: string;
  state: Partial<OnboardingState>;
  components?: UIComponent[];
}

/**
 * Fetch with retry logic for network failures and 401 auth errors
 * Retries up to maxRetries times with exponential backoff
 * On 401, attempts to refresh the token and retry once
 */
async function fetchWithRetry(
  url: string,
  options: RequestInit,
  maxRetries: number = 3,
  baseDelayMs: number = 1000,
  hasTriedRefresh: boolean = false
): Promise<Response> {
  let lastError: Error | null = null;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      const response = await fetch(url, options);

      // Handle 401 Unauthorized - try to refresh token and retry once
      if (response.status === 401 && !hasTriedRefresh) {
        console.log('[CopilotChatProvider] Got 401, attempting token refresh...');
        try {
          const refreshResponse = await auth.refreshToken();
          if (refreshResponse?.tokens?.access?.token) {
            console.log('[CopilotChatProvider] Token refresh successful, retrying request');
            // Update the Authorization header with the new token
            const newOptions = {
              ...options,
              headers: {
                ...options.headers,
                'Authorization': `Bearer ${refreshResponse.tokens.access.token}`,
              },
            };
            // Retry the request with the new token (mark that we've tried refresh)
            return fetchWithRetry(url, newOptions, maxRetries, baseDelayMs, true);
          } else {
            console.log('[CopilotChatProvider] Token refresh returned no token, redirecting to login');
            auth.cleanupSession();
            // Don't redirect to login for public website subdomains
            if (!shouldShowPublicWebsite()) {
              window.location.href = '/login';
            }
            throw new Error('Session expired');
          }
        } catch (refreshError) {
          console.error('[CopilotChatProvider] Token refresh failed:', refreshError);
          auth.cleanupSession();
          // Don't redirect to login for public website subdomains
          if (!shouldShowPublicWebsite()) {
            window.location.href = '/login';
          }
          throw new Error('Session expired');
        }
      }

      return response;
    } catch (error) {
      lastError = error as Error;
      const errorMessage = (error as Error).message?.toLowerCase() || '';

      // If this is a session expired error, don't retry
      if (errorMessage.includes('session expired')) {
        throw error;
      }

      // Check for various network error messages
      const isNetworkError = error instanceof TypeError && (
        errorMessage.includes('fetch') ||
        errorMessage.includes('network') ||
        errorMessage.includes('failed to fetch') ||
        errorMessage.includes('networkerror') ||
        errorMessage.includes('connection')
      );

      // Only retry on network errors, not on other types of errors
      if (!isNetworkError) {
        throw error;
      }

      // Don't retry if this was the last attempt
      if (attempt < maxRetries - 1) {
        const delay = baseDelayMs * Math.pow(2, attempt); // Exponential backoff
        console.log(`[CopilotChatProvider] Network error, retrying in ${delay}ms (attempt ${attempt + 1}/${maxRetries})`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  throw lastError || new Error('Max retries exceeded');
}

export const CopilotChatProvider: React.FC<CopilotChatProviderProps> = ({
  children,
  assistantAlias = 'distillery-assistant',
}) => {
  // Get refreshAccount from UserContext to refresh account data after copilot updates
  const { refreshAccount } = useContext(UserContext);

  // Messages state
  const [messages, setMessages] = useState<IMessage[]>([]);

  // Loading states
  const [isLoading, setIsLoading] = useState(false);
  const [isAssistantWriting, setIsAssistantWriting] = useState(false);

  // Conversation state
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [onboardingState, setOnboardingState] = useState<OnboardingState | null>(null);

  // Copilot UI state
  const [isCopilotOpen, setIsCopilotOpen] = useState(false);
  const [isScreenBlocked, setIsScreenBlocked] = useState(false);

  // Modal states
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [serviceAreaModalOpen, setServiceAreaModalOpen] = useState(false);
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [customerProfileModalOpen, setCustomerProfileModalOpen] = useState(false);

  // Modal prefill data
  const [locationModalPrefill, setLocationModalPrefill] = useState<LocationModalPrefill | null>(null);
  const [serviceAreaModalPrefill, setServiceAreaModalPrefill] = useState<ServiceAreaModalPrefill | null>(null);
  const [productModalPrefill, setProductModalPrefill] = useState<ProductModalPrefill | null>(null);
  const [customerProfileModalPrefill, setCustomerProfileModalPrefill] = useState<CustomerProfileModalPrefill | null>(null);

  // Editing state (shared between Message.tsx and ChatWithModals)
  const [editingLocation, setEditingLocation] = useState<CopilotLocation | null>(null);
  const [editingServiceArea, setEditingServiceArea] = useState<CopilotServiceArea | null>(null);
  const [editingCustomerProfile, setEditingCustomerProfile] = useState<CopilotCustomerProfile | null>(null);

  // Last failed message for retry functionality
  const [lastFailedMessage, setLastFailedMessage] = useState<string | null>(null);

  // Refs for stable references
  const conversationIdRef = useRef<string | null>(null);
  const hasLoadedRef = useRef(false);
  const lastAccountIdRef = useRef<string | null>(null);
  const isStreamingRef = useRef(false); // Prevent concurrent streaming calls
  const isLoadingConversationRef = useRef(false); // Prevent concurrent load/new conversation calls

  // Sync ref with state
  useEffect(() => {
    conversationIdRef.current = conversationId;
  }, [conversationId]);

  // Reset state when account changes (e.g., logout/login with different account)
  useEffect(() => {
    const handleAccountChange = () => {
      const currentAccountId = localStorage.getItem('accountId');

      // Check if account has changed
      if (lastAccountIdRef.current && lastAccountIdRef.current !== currentAccountId) {
        console.log('[CopilotChatProvider] Account changed, resetting chat state');
        // Reset all state for new account
        setMessages([]);
        setConversationId(null);
        setOnboardingState(null);
        hasLoadedRef.current = false;
        lastAccountIdRef.current = currentAccountId;
        // Load conversation for new account
        loadActiveConversation();
      } else if (!lastAccountIdRef.current && currentAccountId) {
        // First time setting account ID
        lastAccountIdRef.current = currentAccountId;
      }
    };

    // Listen for storage events (account changes)
    window.addEventListener('storage', handleAccountChange);

    // Also check periodically for account changes (for same-tab changes)
    const intervalId = setInterval(handleAccountChange, 1000);

    return () => {
      window.removeEventListener('storage', handleAccountChange);
      clearInterval(intervalId);
    };
  }, []);

  // Load active conversation on mount
  useEffect(() => {
    const currentAccountId = localStorage.getItem('accountId');
    lastAccountIdRef.current = currentAccountId;

    if (hasLoadedRef.current) return;
    hasLoadedRef.current = true;
    loadActiveConversation();
  }, []);

  // Also reload if the component has no messages after mount (e.g., after login)
  // Use a ref to track if we've already attempted a reload to prevent infinite loops
  const hasAttemptedReloadRef = useRef(false);
  useEffect(() => {
    // Only trigger if we've already tried to load once but have no messages and aren't loading
    // Also check that we haven't already attempted a reload
    if (hasLoadedRef.current && messages.length === 0 && !isLoading && !conversationId && !hasAttemptedReloadRef.current) {
      hasAttemptedReloadRef.current = true;
      console.log('[CopilotChatProvider] No messages after load, attempting to reload conversation');
      loadActiveConversation();
    }
    // Reset the reload flag when we get messages or a conversationId
    if (messages.length > 0 || conversationId) {
      hasAttemptedReloadRef.current = false;
    }
  }, [messages.length, isLoading, conversationId]);

  // Note: Locations and service areas are now created immediately in the backend
  // when scraped from the website (in handleWebsiteStep). They arrive with MongoDB IDs.
  // No need to create them from the frontend anymore.

  /**
   * Start a new conversation (used by loadActiveConversation when no active conversation exists)
   * Note: This is called from within loadActiveConversation, so ref management is handled there
   */
  const startNewConversation = useCallback(async () => {
    // Set the step to 'welcome' so ThinkingMessage shows appropriate messages
    setOnboardingState((prev) => prev ? { ...prev, currentStep: 'welcome' } : { currentStep: 'welcome' } as OnboardingState);
    setIsLoading(true);
    try {
      const response = await http.post<{
        success: boolean;
        data: ChatResponse;
      }>('/v1/copilot/chat/new');

      if (response.data.success) {
        const { message, conversationId: newConvId, state } = response.data.data;

        setConversationId(newConvId);
        setOnboardingState(state as OnboardingState);

        if (message) {
          const welcomeMessage: IMessage = {
            message_id: `assistant-${Date.now()}`,
            raw_message: message,
            role: ThreadRolesEnum.Assistant,
            streaming: false,
            error: false,
            hidden: false,
          };
          setMessages([welcomeMessage]);
        }
      }
    } catch (error) {
      console.error('[CopilotChatProvider] Failed to start new conversation:', error);
      throw error; // Re-throw so loadActiveConversation can handle it
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Load active conversation from backend
   */
  const loadActiveConversation = useCallback(async () => {
    // Don't make API call if not authenticated - prevents redirect loop
    const tokens = auth.getTokens();
    if (!tokens) {
      console.log('[CopilotChatProvider] No auth tokens, skipping load');
      return;
    }

    // Prevent concurrent calls - if already loading, skip
    if (isLoadingConversationRef.current) {
      console.log('[CopilotChatProvider] loadActiveConversation skipped - already in progress');
      return;
    }
    isLoadingConversationRef.current = true;

    console.log('[CopilotChatProvider] Loading active conversation...');
    setIsLoading(true);
    setOnboardingState((prev) => prev ? { ...prev, currentStep: 'resume' } : { currentStep: 'resume' } as OnboardingState);

    try {
      const response = await http.get<{
        success: boolean;
        data: {
          conversationId: string;
          state: OnboardingState;
          messages: Array<{
            id: string;
            role: 'user' | 'assistant' | 'system';
            content: string;
            timestamp: string;
            metadata?: { hidden?: boolean };
          }>;
        } | null;
      }>('/v1/copilot/chat/state');

      if (response.data.success && response.data.data) {
        const { conversationId: convId, state, messages: historyMessages } = response.data.data;

        setConversationId(convId);
        setOnboardingState(state);

        // Transform messages to UI format, filtering out hidden messages
        const uiMessages: IMessage[] = historyMessages
          .filter((msg) => !msg.metadata?.hidden)
          .map((msg) => ({
            message_id: msg.id,
            raw_message: msg.content,
            role: msg.role === 'assistant' ? ThreadRolesEnum.Assistant : ThreadRolesEnum.User,
            streaming: false,
            error: false,
            hidden: false,
          }));

        // If conversation exists but has no visible messages, start fresh
        if (uiMessages.length === 0) {
          console.log('[CopilotChatProvider] Conversation has no visible messages, starting new chat');
          await startNewConversation();
        } else {
          setMessages(uiMessages);
          setIsLoading(false);
        }
      } else {
        // No active conversation, auto-start a new one
        console.log('[CopilotChatProvider] No active conversation, starting new chat');
        await startNewConversation();
      }
    } catch (error) {
      console.error('[CopilotChatProvider] Failed to load active conversation:', error);
      // On error, try to start a new conversation anyway
      try {
        await startNewConversation();
      } catch (newConvError) {
        console.error('[CopilotChatProvider] Failed to start new conversation:', newConvError);
        setIsLoading(false);
        // Show a default welcome message so the user isn't stuck with empty chat
        const fallbackMessage: IMessage = {
          message_id: `assistant-${Date.now()}`,
          raw_message: "Hi! I'm here to help you create your marketing campaign. What would you like to do today?",
          role: ThreadRolesEnum.Assistant,
          streaming: false,
          error: false,
          hidden: false,
        };
        setMessages([fallbackMessage]);
      }
    } finally {
      isLoadingConversationRef.current = false;
    }
  }, []);

  /**
   * Handle UI components from response (open modals, etc.)
   */
  const handleUIComponents = useCallback((components?: UIComponent[]) => {
    if (!components) return;

    console.log('[CopilotChatProvider] handleUIComponents called with:', components.length, 'components');

    for (const component of components) {
      console.log('[CopilotChatProvider] Processing component:', component.type, component.props?.action);
      if (component.type === 'bordered-container' && component.props.action) {
        switch (component.props.action) {
          case 'open_location_modal': {
            console.log('[CopilotChatProvider] Opening location modal');

            // Capture prefill data from the component
            const locationData = component.props.data as { name?: string; address?: string } | undefined;
            if (locationData && (locationData.name || locationData.address)) {
              setLocationModalPrefill({
                name: locationData.name,
                address: locationData.address,
              });
            }
            setLocationModalOpen(true);
            break;
          }
          case 'open_service_area_modal': {
            console.log('[CopilotChatProvider] Opening service area modal with data:', component.props.data);
            // Capture prefill data from the component
            const saData = component.props.data as {
              type?: 'radius' | 'geographic';
              address?: string;
              radius?: number;
              name?: string;
              geographic?: {
                entityType: 'neighborhood' | 'city' | 'county' | 'state' | 'country';
                entityName: string;
                displayName?: string;
              };
            } | undefined;

            if (saData) {
              if (saData.type === 'geographic' && saData.geographic) {
                // Handle geographic type prefill
                setServiceAreaModalPrefill({
                  type: 'geographic',
                  name: saData.name, // Pass the area name
                  geographic: {
                    entityType: saData.geographic.entityType,
                    entityName: saData.geographic.entityName,
                    displayName: saData.geographic.displayName || saData.geographic.entityName,
                  },
                });
              } else if (saData.address || saData.radius || saData.name) {
                // Handle radius type prefill
                setServiceAreaModalPrefill({
                  type: 'radius',
                  name: saData.name, // Pass the area name (e.g., "30 miles from Buenos Aires")
                  address: saData.address, // Pass the address separately
                  radius: saData.radius,
                });
              }
            }
            console.log('[CopilotChatProvider] Setting serviceAreaModalOpen to true');
            setServiceAreaModalOpen(true);
            break;
          }
          case 'open_product_modal': {
            // Capture prefill data from the component
            const data = component.props.data as {
              name?: string;
              description?: string;
              type?: 'product' | 'service';
              url?: string;
            } | undefined;
            if (data && (data.name || data.description || data.type || data.url)) {
              setProductModalPrefill({
                name: data.name,
                description: data.description,
                type: data.type,
                url: data.url,
              });
            }
            setProductModalOpen(true);
            break;
          }
          case 'open_customer_profile_modal': {
            // Capture prefill data from the component
            const profileData = component.props.data as CustomerProfileModalPrefill | undefined;
            console.log('🟣🟣🟣 [CopilotChatProvider] open_customer_profile_modal - Raw component.props:', JSON.stringify(component.props, null, 2));
            console.log('🟣🟣🟣 [CopilotChatProvider] open_customer_profile_modal - profileData:', JSON.stringify({
              name: profileData?.name,
              description: profileData?.description,
              customer_rules: profileData?.customer_rules,
              customer_tones: profileData?.customer_tones,
              hasRules: !!profileData?.customer_rules?.length,
              hasTones: !!profileData?.customer_tones?.length,
            }, null, 2));
            // Set prefill if we have any meaningful data (name, description, or rules)
            if (profileData && (profileData.name || profileData.description || profileData.customer_rules?.length)) {
              setCustomerProfileModalPrefill(profileData);
              console.log('🟣🟣🟣 [CopilotChatProvider] Setting customerProfileModalPrefill');
            } else {
              console.log('🟣🟣🟣 [CopilotChatProvider] NOT setting prefill - condition not met');
            }
            setCustomerProfileModalOpen(true);
            break;
          }
        }
      }
    }
  }, []);

  /**
   * Send a message to the copilot with SSE streaming
   */
  const sendMessage = useCallback(async (
    text: string,
    _role?: string,
    hidden?: boolean,
    _skipScroll?: boolean,
    _retryCount?: number
  ) => {
    const retryCount = _retryCount || 0;
    const maxRetries = 3;

    // Prevent concurrent streaming calls
    console.log('[CopilotChatProvider] sendMessage guard check - isStreamingRef:', isStreamingRef.current);
    if (isStreamingRef.current) {
      console.log('[CopilotChatProvider] Skipping duplicate streaming call');
      return;
    }
    console.log('[CopilotChatProvider] Guard passed, proceeding with message');

    // Don't make API call if not authenticated
    const tokens = auth.getTokens();
    if (!tokens) {
      console.log('[CopilotChatProvider] No auth tokens, cannot send message');
      return;
    }

    if (!text.trim() && conversationIdRef.current) {
      return;
    }

    isStreamingRef.current = true;

    // Optimistically update currentStep FIRST, BEFORE isLoading, so ThinkingMessage shows the right messages
    console.log('[CopilotChatProvider] sendMessage called - current onboardingState:', {
      currentStep: onboardingState?.currentStep,
      businessName: onboardingState?.businessName,
      businessDescription: onboardingState?.businessDescription,
      industry: onboardingState?.industry,
      completedSteps: onboardingState?.completedSteps,
    });

    // Show generic "Thinking..." until backend sends step_start with the actual step
    // Only the LLM knows what the user is asking for - frontend should not guess
    const initialStep = 'thinking';
    console.log('[CopilotChatProvider] Setting initial step to thinking, waiting for backend step_start');

    setOnboardingState((prev) => {
      console.log('[CopilotChatProvider] setOnboardingState callback - prev:', prev?.currentStep, '-> new:', initialStep);
      return prev ? { ...prev, currentStep: initialStep } : prev;
    });

    // Now set loading states AFTER step is updated
    setIsLoading(true);
    setIsAssistantWriting(true);

    // Add user message to UI immediately (unless hidden)
    if (!hidden && text.trim()) {
      const userMessage: IMessage = {
        message_id: `user-${Date.now()}`,
        raw_message: text,
        role: ThreadRolesEnum.User,
        streaming: false,
        error: false,
        hidden: false,
      };
      setMessages((prev) => [...prev, userMessage]);
    }

    // Message ID for streaming - will be added on first text event
    const streamingMessageId = `assistant-${Date.now()}`;
    let messageAdded = false;

    try {
      // Get account ID for header
      const accountId = localStorage.getItem('accountId') || '';

      // Use fetch with retry for SSE streaming
      const response = await fetchWithRetry(
        `${import.meta.env.VITE_API_URL}/copilot/chat/stream`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${tokens.access.token}`,
            'x-account-id': accountId,
          },
          body: JSON.stringify({
            message: text,
            conversationId: conversationIdRef.current,
          }),
        },
        3, // maxRetries
        1000 // baseDelayMs
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No response body');
      }

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Process SSE events
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);

            if (data === '[DONE]') {
              // Streaming complete
              continue;
            }

            try {
              const event = JSON.parse(data);

              if (event.type === 'step_start') {
                // Update the current step immediately so ThinkingMessage shows the right messages
                // In an agentic flow, there can be multiple tool calls (name → description → industry)
                // each with their own step, so we should always update to the latest step
                const { currentStep } = event;
                console.log('[CopilotChatProvider] Step started:', currentStep);
                setOnboardingState((prev) => ({
                  ...prev,
                  currentStep,
                }) as typeof prev);
              } else if (event.type === 'text') {
                // Add or update streaming message with new content
                if (!messageAdded) {
                  // First text event - add the message
                  messageAdded = true;
                  setMessages((prev) => [...prev, {
                    message_id: streamingMessageId,
                    raw_message: event.content,
                    role: ThreadRolesEnum.Assistant,
                    streaming: true,
                    error: false,
                    hidden: false,
                  }]);
                } else {
                  // Update existing message
                  setMessages((prev) =>
                    prev.map((msg) =>
                      msg.message_id === streamingMessageId
                        ? { ...msg, raw_message: event.content }
                        : msg
                    )
                  );
                }
              } else if (event.type === 'complete') {
                // Final complete event with state and components
                const { message, conversationId: newConvId, state, components } = event;
                console.log('[CopilotChatProvider] Complete event - backend state:', {
                  currentStep: state?.currentStep,
                  businessName: state?.businessName,
                  completedSteps: state?.completedSteps,
                  generatedLogosCount: state?.generatedLogos?.length || 0,
                  generatedLogos: state?.generatedLogos?.map((l: { id: string; style: string; url?: string }) => ({ id: l.id, style: l.style, url: l.url?.substring(0, 50) + '...' })),
                  logoUrl: state?.logoUrl,
                });

                // Update conversation ID if new
                if (newConvId && newConvId !== conversationIdRef.current) {
                  setConversationId(newConvId);
                }

                // Update state - preserve currentStep from optimistic update if backend doesn't provide one
                if (state) {
                  setOnboardingState((prev) => {
                    console.log('[CopilotChatProvider] Complete event - prev state logos:', {
                      prevGeneratedLogosCount: prev?.generatedLogos?.length || 0,
                      prevLogos: prev?.generatedLogos?.map((l: { id: string; style: string }) => ({ id: l.id, style: l.style })),
                    });
                    const newState = {
                      currentStep: 'welcome',
                      completedSteps: [],
                      catalogs: [],
                      locations: [],
                      serviceAreas: [],
                      customerProfiles: [],
                      ...(prev || {}),
                      ...state,
                    };
                    // If backend returns 'welcome' but we have a better step, preserve it
                    if (state.currentStep === 'welcome' && prev?.currentStep && prev.currentStep !== 'welcome') {
                      console.log('[CopilotChatProvider] Preserving optimistic currentStep:', prev.currentStep, 'instead of backend:', state.currentStep);
                      newState.currentStep = prev.currentStep;
                    }
                    console.log('[CopilotChatProvider] Complete event - final state:', {
                      currentStep: newState.currentStep,
                      generatedLogosCount: newState.generatedLogos?.length || 0,
                      logos: newState.generatedLogos?.map((l: { id: string; style: string }) => ({ id: l.id, style: l.style })),
                      catalogsCount: newState.catalogs?.length || 0,
                      catalogs: newState.catalogs?.map((c: { catalogId?: string; name?: string }) => ({ catalogId: c.catalogId, name: c.name })),
                    });
                    console.log('[CopilotChatProvider] Backend state.catalogs:', state.catalogs);

                    // Check if any account-related field was newly confirmed
                    // This happens when confirm_field saves data to the account
                    const accountFields = ['logo', 'name', 'description', 'industry', 'phone', 'channel', 'locations', 'serviceAreas'];
                    const prevCompletedSteps = prev?.completedSteps || [];
                    const newCompletedSteps = state.completedSteps || [];
                    const newlyCompleted = newCompletedSteps.filter((step: string) => !prevCompletedSteps.includes(step));

                    if (newlyCompleted.some((step: string) => accountFields.includes(step))) {
                      console.log('[CopilotChatProvider] Account field confirmed, refreshing account data:', newlyCompleted);
                      // Refresh account data so other parts of the app see the updated values
                      refreshAccount();
                    }

                    return newState;
                  });
                }

                // Finalize the streaming message (add if not yet added, or update existing)
                if (!messageAdded) {
                  messageAdded = true;
                  setMessages((prev) => [...prev, {
                    message_id: streamingMessageId,
                    raw_message: message,
                    role: ThreadRolesEnum.Assistant,
                    streaming: false,
                    error: false,
                    hidden: false,
                  }]);
                } else {
                  setMessages((prev) =>
                    prev.map((msg) =>
                      msg.message_id === streamingMessageId
                        ? { ...msg, raw_message: message, streaming: false }
                        : msg
                    )
                  );
                }

                // Handle UI component actions
                console.log('[CopilotChatProvider] Complete event - components:', components);
                handleUIComponents(components);
              } else if (event.type === 'logo_progress') {
                // Handle progressive logo generation
                const { logo, index, total } = event;
                console.log(`[CopilotChatProvider] Received logo ${index + 1}/${total}:`, logo.style, 'url:', logo.url?.substring(0, 50) + '...');

                // Update onboarding state with the new logo progressively
                setOnboardingState((prev) => {
                  // If this is the first logo (index === 0), clear old logos and start fresh
                  // Otherwise, add to existing logos (but avoid duplicates)
                  if (index === 0) {
                    console.log('[CopilotChatProvider] First logo of new batch, clearing old logos');
                    return {
                      ...prev,
                      generatedLogos: [logo],
                      pendingLogoCount: total - 1,
                    } as typeof prev;
                  }

                  const existingLogos = prev?.generatedLogos || [];
                  // Avoid duplicates - check if logo with same id already exists
                  const logoExists = existingLogos.some((l: { id: string }) => l.id === logo.id);
                  if (logoExists) {
                    return prev;
                  }
                  return {
                    ...prev,
                    generatedLogos: [...existingLogos, logo],
                    pendingLogoCount: total - (existingLogos.length + 1), // How many are still pending
                  } as typeof prev;
                });

                // If this is the first logo and no message added yet, add a placeholder message
                if (index === 0 && !messageAdded) {
                  messageAdded = true;
                  setMessages((prev) => [...prev, {
                    message_id: streamingMessageId,
                    raw_message: `Generating ${total} logo options...`,
                    role: ThreadRolesEnum.Assistant,
                    streaming: true,
                    error: false,
                    hidden: false,
                  }]);
                }
              } else if (event.type === 'error') {
                // Handle error event
                if (!messageAdded) {
                  messageAdded = true;
                  setMessages((prev) => [...prev, {
                    message_id: streamingMessageId,
                    raw_message: event.message || 'An error occurred.',
                    role: ThreadRolesEnum.Assistant,
                    streaming: false,
                    error: true,
                    hidden: false,
                  }]);
                } else {
                  setMessages((prev) =>
                    prev.map((msg) =>
                      msg.message_id === streamingMessageId
                        ? { ...msg, raw_message: event.message || 'An error occurred.', error: true, streaming: false }
                        : msg
                    )
                  );
                }
              }
            } catch (parseError) {
              console.error('[CopilotChatProvider] Failed to parse SSE event:', parseError);
            }
          }
        }
      }
    } catch (error) {
      const errorMessage = (error as Error).message?.toLowerCase() || '';
      const isNetworkError = error instanceof TypeError && (
        errorMessage.includes('fetch') ||
        errorMessage.includes('network') ||
        errorMessage.includes('failed to fetch') ||
        errorMessage.includes('networkerror') ||
        errorMessage.includes('connection')
      );

      // Silently retry on network errors
      if (isNetworkError && retryCount < maxRetries) {
        console.log(`[CopilotChatProvider] Network error, retrying (${retryCount + 1}/${maxRetries})...`);
        // Remove the streaming message if it was added
        if (messageAdded) {
          setMessages((prev) => prev.filter((msg) => msg.message_id !== streamingMessageId));
        }
        // Wait before retrying
        const delay = 1000 * Math.pow(2, retryCount);
        await new Promise(resolve => setTimeout(resolve, delay));
        // Retry silently (hidden=true on retry to avoid duplicate user messages)
        isStreamingRef.current = false; // Reset before recursive call
        setIsLoading(false);
        setIsAssistantWriting(false);
        return sendMessage(text, _role, true, _skipScroll, retryCount + 1);
      }

      console.error('[CopilotChatProvider] Failed to send message:', error);

      // Store the failed message for retry
      setLastFailedMessage(text);

      // Show error message with retry action
      const errorMessageContent = isNetworkError
        ? 'I couldn\'t connect to the server. Please check your connection and try again.\n\n<retry-action />'
        : 'Sorry, I encountered an error. Please try again.\n\n<retry-action />';

      // Show error message to user (only after all retries exhausted)
      // Check if the streaming message still exists, if not add a new error message
      setMessages((prev) => {
        const messageExists = prev.some((msg) => msg.message_id === streamingMessageId);
        if (messageExists) {
          // Update existing streaming message with error
          return prev.map((msg) =>
            msg.message_id === streamingMessageId
              ? { ...msg, raw_message: errorMessageContent, error: true, streaming: false }
              : msg
          );
        } else {
          // Add new error message since the streaming message was removed during retries
          return [...prev, {
            message_id: streamingMessageId,
            raw_message: errorMessageContent,
            role: ThreadRolesEnum.Assistant,
            streaming: false,
            error: true,
            hidden: false,
          }];
        }
      });
    } finally {
      console.log('[CopilotChatProvider] sendMessage finally block - resetting isStreamingRef');
      isStreamingRef.current = false; // Reset streaming guard
      setIsLoading(false);
      setIsAssistantWriting(false);
    }
  }, []);

  /**
   * Retry the last failed message
   */
  const retryLastMessage = useCallback(() => {
    if (!lastFailedMessage) {
      console.log('[CopilotChatProvider] No failed message to retry');
      return;
    }

    console.log('[CopilotChatProvider] Retrying last failed message:', lastFailedMessage);

    // Remove the error message from the chat
    setMessages((prev) => prev.filter((msg) => !msg.error));

    // Clear the failed message state
    const messageToRetry = lastFailedMessage;
    setLastFailedMessage(null);

    // Resend the message (hidden=true to avoid duplicate user message since it's already in the chat)
    sendMessage(messageToRetry, undefined, true);
  }, [lastFailedMessage, sendMessage]);

  /**
   * Reset chat and start new conversation
   */
  const resetChat = useCallback(async () => {
    // Don't make API call if not authenticated
    if (!auth.getTokens()) {
      console.log('[CopilotChatProvider] No auth tokens, cannot reset chat');
      return;
    }

    // Prevent concurrent calls
    if (isLoadingConversationRef.current) {
      console.log('[CopilotChatProvider] resetChat skipped - already in progress');
      return;
    }
    isLoadingConversationRef.current = true;

    // Set the step to 'welcome' so ThinkingMessage shows appropriate messages
    setOnboardingState((prev) => prev ? { ...prev, currentStep: 'welcome' } : { currentStep: 'welcome' } as OnboardingState);
    setIsLoading(true);
    setMessages([]);

    try {
      const response = await http.post<{
        success: boolean;
        data: ChatResponse;
      }>('/v1/copilot/chat/new');

      if (response.data.success) {
        const { message, conversationId: newConvId, state, components } = response.data.data;

        setConversationId(newConvId);
        setOnboardingState(state as OnboardingState);

        if (message) {
          const welcomeMessage: IMessage = {
            message_id: `assistant-${Date.now()}`,
            raw_message: message,
            role: ThreadRolesEnum.Assistant,
            streaming: false,
            error: false,
            hidden: false,
          };
          setMessages([welcomeMessage]);
        }

        handleUIComponents(components);
      }
    } catch (error) {
      console.error('[CopilotChatProvider] Failed to reset chat:', error);
    } finally {
      setIsLoading(false);
      isLoadingConversationRef.current = false;
    }
  }, [handleUIComponents]);

  /**
   * Send modal event to backend
   */
  const sendModalEvent = useCallback(async (
    eventType: string,
    data?: Record<string, unknown>
  ) => {
    // Don't make API call if not authenticated
    if (!auth.getTokens()) {
      return;
    }
    if (!conversationIdRef.current) return;

    try {
      const response = await http.post<{
        success: boolean;
        data: ChatResponse;
      }>('/v1/copilot/chat/event', {
        conversationId: conversationIdRef.current,
        eventType,
        data,
      });

      if (response.data.success) {
        const { message, state, components } = response.data.data;

        if (state) {
          setOnboardingState((prev) => ({
            currentStep: 'welcome',
            completedSteps: [],
            catalogs: [],
            locations: [],
            serviceAreas: [],
            customerProfiles: [],
            ...(prev || {}),
            ...state,
          }));
        }

        if (message) {
          const assistantMessage: IMessage = {
            message_id: `assistant-${Date.now()}`,
            raw_message: message,
            role: ThreadRolesEnum.Assistant,
            streaming: false,
            error: false,
            hidden: false,
          };
          setMessages((prev) => [...prev, assistantMessage]);
        }

        handleUIComponents(components);
      }
    } catch (error) {
      console.error(`[CopilotChatProvider] Failed to handle ${eventType}:`, error);
    }
  }, [handleUIComponents]);

  // Data modification handlers
  const addLocation = useCallback((location: CopilotLocation) => {
    setOnboardingState((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        locations: [...prev.locations, { ...location, id: location.id || `loc-${Date.now()}` }],
      };
    });
    sendModalEvent('location_added', location as unknown as Record<string, unknown>);
  }, [sendModalEvent]);

  const updateLocation = useCallback((id: string, location: CopilotLocation) => {
    setOnboardingState((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        locations: prev.locations.map((loc) =>
          (loc.id === id || loc.name === id) ? { ...location, id: location.id || loc.id || id } : loc
        ),
      };
    });
    sendModalEvent('location_updated', location as unknown as Record<string, unknown>);
  }, [sendModalEvent]);

  const addServiceArea = useCallback((serviceArea: CopilotServiceArea) => {
    setOnboardingState((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        serviceAreas: [...prev.serviceAreas, { ...serviceArea, id: serviceArea.id || `sa-${Date.now()}` }],
      };
    });
    sendModalEvent('service_area_added', serviceArea as unknown as Record<string, unknown>);
  }, [sendModalEvent]);

  const updateServiceArea = useCallback((id: string, serviceArea: CopilotServiceArea) => {
    setOnboardingState((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        serviceAreas: prev.serviceAreas.map((sa) =>
          (sa.id === id || sa.name === id) ? { ...serviceArea, id: sa.id || id } : sa
        ),
      };
    });
    sendModalEvent('service_area_updated', serviceArea as unknown as Record<string, unknown>);
  }, [sendModalEvent]);

  const addCatalog = useCallback((catalog: CopilotCatalog) => {
    setOnboardingState((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        catalogs: [...prev.catalogs, catalog],
      };
    });
    sendModalEvent('product_added', catalog as unknown as Record<string, unknown>);
  }, [sendModalEvent]);

  const addCustomerProfile = useCallback((profile: CopilotCustomerProfile) => {
    setOnboardingState((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        customerProfiles: [...prev.customerProfiles, profile],
      };
    });
    sendModalEvent('customer_profile_added', profile as unknown as Record<string, unknown>);
  }, [sendModalEvent]);

  const updateCustomerProfile = useCallback((id: string, profile: CopilotCustomerProfile) => {
    setOnboardingState((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        customerProfiles: prev.customerProfiles.map((p) =>
          p._id === id ? { ...profile, _id: id } : p
        ),
      };
    });
    sendModalEvent('customer_profile_updated', profile as unknown as Record<string, unknown>);
  }, [sendModalEvent]);

  // Domain selection - sends event to backend to register domain
  const selectDomain = useCallback((domain: string) => {
    sendModalEvent('domain_selected', { domain });
  }, [sendModalEvent]);

  const removeLocation = useCallback((id: string) => {
    setOnboardingState((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        locations: prev.locations.filter((loc) => loc.id !== id && loc.name !== id),
      };
    });
    // Don't send event for removals - let user confirm the original question
  }, []);

  const removeServiceArea = useCallback((id: string) => {
    setOnboardingState((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        serviceAreas: prev.serviceAreas.filter((sa) => sa.id !== id && sa.name !== id),
      };
    });
    // Don't send event for removals - let user confirm the original question
  }, []);

  const removeCatalog = useCallback((id: string) => {
    setOnboardingState((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        catalogs: prev.catalogs.filter((c) => c.catalogId !== id && c.id !== id),
        selectedCatalogId: prev.selectedCatalogId === id ? undefined : prev.selectedCatalogId,
      };
    });
    // Don't send event for removals - let user confirm the original question
  }, []);

  const removeCustomerProfile = useCallback((id: string) => {
    setOnboardingState((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        customerProfiles: prev.customerProfiles.filter((p) => p._id !== id),
      };
    });
    // Don't send event for removals - let user confirm the original question
  }, []);

  const setSelectedCatalogId = useCallback((id: string | null) => {
    setOnboardingState((prev) => {
      if (!prev) return prev;
      return { ...prev, selectedCatalogId: id || undefined };
    });
  }, []);

  // Clear prefill data
  const clearLocationModalPrefill = useCallback(() => {
    setLocationModalPrefill(null);
  }, []);

  const clearServiceAreaModalPrefill = useCallback(() => {
    setServiceAreaModalPrefill(null);
  }, []);

  const clearProductModalPrefill = useCallback(() => {
    setProductModalPrefill(null);
  }, []);

  const clearCustomerProfileModalPrefill = useCallback(() => {
    setCustomerProfileModalPrefill(null);
  }, []);

  // UI handlers
  const openCopilot = useCallback((block = false) => {
    setIsCopilotOpen(true);
    setIsScreenBlocked(block);
  }, []);

  const closeCopilot = useCallback(() => {
    setIsCopilotOpen(false);
    setIsScreenBlocked(false);
  }, []);

  // Computed values
  const shouldShowTypingIndicator = isAssistantWriting || isLoading;

  // Memoize context value
  const contextValue: CopilotChatContextType = useMemo(
    () => ({
      // Connection state
      isConnected: true,
      isAssistantWriting,
      shouldShowTypingIndicator,
      isLoading,
      setIsLoading,
      isFunctionCallInProgress: false,
      isWaitingForAssistant: isAssistantWriting,

      // Messages
      messages,

      // Actions
      sendMessage,
      resetChat,
      retryLastMessage,
      lastFailedMessage,

      // Copilot UI state
      isCopilotOpen,
      openCopilot,
      closeCopilot,
      isScreenBlocked,

      // Legacy compatibility
      countryConfig: null,
      assistantAlias,

      // Conversation state
      conversationId,
      onboardingState,

      // Modal state
      locationModalOpen,
      serviceAreaModalOpen,
      productModalOpen,
      customerProfileModalOpen,
      setLocationModalOpen,
      setServiceAreaModalOpen,
      setProductModalOpen,
      setCustomerProfileModalOpen,

      // Modal prefill data
      locationModalPrefill,
      clearLocationModalPrefill,
      serviceAreaModalPrefill,
      clearServiceAreaModalPrefill,
      productModalPrefill,
      clearProductModalPrefill,
      customerProfileModalPrefill,
      clearCustomerProfileModalPrefill,

      // Chat data
      currentLocations: onboardingState?.locations || [],
      currentServiceAreas: onboardingState?.serviceAreas || [],
      currentCatalogs: onboardingState?.catalogs || [],
      currentCustomerProfiles: onboardingState?.customerProfiles || [],
      selectedChannel: onboardingState?.channel || null,

      // Data setters
      addLocation,
      updateLocation,
      addServiceArea,
      updateServiceArea,
      addCatalog,
      addCustomerProfile,
      updateCustomerProfile,
      removeLocation,
      removeServiceArea,
      removeCatalog,
      removeCustomerProfile,
      setSelectedCatalogId,

      // Editing state
      editingLocation,
      setEditingLocation,
      editingServiceArea,
      setEditingServiceArea,
      editingCustomerProfile,
      setEditingCustomerProfile,

      // Domain selection
      selectDomain,
    }),
    [
      isAssistantWriting,
      shouldShowTypingIndicator,
      isLoading,
      messages,
      sendMessage,
      resetChat,
      retryLastMessage,
      lastFailedMessage,
      isCopilotOpen,
      openCopilot,
      closeCopilot,
      isScreenBlocked,
      assistantAlias,
      conversationId,
      onboardingState,
      locationModalOpen,
      serviceAreaModalOpen,
      productModalOpen,
      customerProfileModalOpen,
      locationModalPrefill,
      clearLocationModalPrefill,
      serviceAreaModalPrefill,
      clearServiceAreaModalPrefill,
      productModalPrefill,
      clearProductModalPrefill,
      customerProfileModalPrefill,
      clearCustomerProfileModalPrefill,
      addLocation,
      updateLocation,
      addServiceArea,
      updateServiceArea,
      addCatalog,
      addCustomerProfile,
      updateCustomerProfile,
      removeLocation,
      removeServiceArea,
      removeCatalog,
      removeCustomerProfile,
      setSelectedCatalogId,
      editingLocation,
      editingServiceArea,
      editingCustomerProfile,
      selectDomain,
    ]
  );

  return (
    <CopilotChatContext.Provider value={contextValue}>
      {children}
    </CopilotChatContext.Provider>
  );
};

export default CopilotChatProvider;
